import { Button } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { PublicPage } from 'components/templates';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMutateOptions, useResetPasswordNavigation } from 'hooks';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiCaretLeft, PiKeyhole, PiPaperPlaneTilt } from 'react-icons/pi';
import { useRequestResetPassword } from 'services/auth/resetPassword';
import { IRequestResetPasswordForm } from 'services/auth/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { object, string } from 'yup';

export const resetPasswordRequestValidationSchema = object().shape({
  email: string()
    .email(i18next.t('formik-error.invalid-email'))
    .required(i18next.t('formik-error.required')),
});

const ResetPasswordRequest = () => {
  const { t } = useTranslation();

  const { goToLogin, email, goToRequestAgain } = useResetPasswordNavigation();

  const requestPasswordReset = useRequestResetPassword();

  const mutateOptions = useMutateOptions<IRequestResetPasswordForm>();

  const onResetPasswordRequest = async (
    values: IRequestResetPasswordForm,
    helpers: FormikHelpers<IRequestResetPasswordForm>
  ) => {
    await requestPasswordReset.mutateAsync(values, {
      ...mutateOptions(helpers),
      onSuccess: () => goToRequestAgain(values.email),
    });
  };

  return (
    <PublicPage
      header={
        <>
          <PiKeyhole />
          {t('reset-password-request.header')}
        </>
      }
      footer={
        <div
          className='flex gap-2 cursor-pointer p-4 items-center'
          onClick={goToLogin}>
          <PiCaretLeft />
          {t('reset-password-request.back')}
        </div>
      }>
      <div className=' w-72 opacity-65'>
        {t('reset-password-request.message')}
      </div>
      <Formik
        validateOnBlur
        validationSchema={resetPasswordRequestValidationSchema}
        initialValues={{ email }}
        enableReinitialize
        onSubmit={onResetPasswordRequest}>
        <Form>
          <FormInput.Text
            name='email'
            type='email'
            label={t('input.label.email')}
          />
          <Button
            type='submit'
            variant={BUTTON_VARIANT.submit}
            isLoading={requestPasswordReset.isPending}
            fullWidth>
            <Button.Text text={t('reset-password-request.button')} />
            <Button.Icon icon={<PiPaperPlaneTilt />} />
          </Button>
        </Form>
      </Formik>
    </PublicPage>
  );
};

export default ResetPasswordRequest;
